import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { DataFormatService } from '../services/data-format.service';
import { Subscription } from 'rxjs';
import { get_committee_dashboard_by_date } from './graphql/getCommitteeDashboardByDate';
import { get_committee_dashboard_by_project } from '../deals/deals-committee-dashboard/graphql/getCommitteeDashboardByProject';
import moment from 'moment';
import { formatCurrency } from '@angular/common';
import CommitteeVoteType from './committee-votes/committee-vote/committee-vote-type';
import Milestone from '../interfaces/milestone';
import { CommitteeApprovalType } from './committee-approvals/committee-approval-type';
import { CommitteeItemsService } from './committee-items/committee-items.service';
import { CommitteeApprovalDashboardService } from './committee-approval-dashboard.service';
import Project from '../interfaces/project';
import ProjectDisclosure from '../interfaces/project-disclosure';
import { CommitteeApprovalsService } from './committee-approvals/committee-approvals.service';
import { EmbeddedReportService } from '../components/embedded-report/embedded-report.service';
import committeeItems from "./committee-items/graphql/committee-items";
import CommitteeVote from "./committee-votes/committee-vote/committee-vote";

const GET_COMMITTEE_DASHBOARD_BY_DATE = get_committee_dashboard_by_date;
const GET_COMMITTEE_DASHBOARD_BY_PROJECT = get_committee_dashboard_by_project;

@Component({
    selector: 'committee-approval-dashboard',
    templateUrl: './committee-approval-dashboard.component.html',
    styleUrls: ['./committee-approval-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommitteeApprovalDashboardComponent implements OnInit {
  @Input() projectid: string;

  constructor(
    public apollo: Apollo,
    private dataFormat: DataFormatService,
    private dashboardService: CommitteeApprovalDashboardService,
    private committeeApprovalsService: CommitteeApprovalsService,
    private route: ActivatedRoute,
    private router: Router,
    private committeeItemsService: CommitteeItemsService,
    public embeddedReportService: EmbeddedReportService
  ) {}

  info: any = [];
  isValidDate: boolean = false;
  formattedDate: string;
  waiting: boolean = false;
  readOnly: boolean = true;
  voteTypes: CommitteeVoteType[];
  milestones: Milestone[];
  closingMilestone: Milestone
  approvalTypes: CommitteeApprovalType[] = [];
  querySubscription: Subscription;
  showReport = false;
  reportProject: string = '';
  ngOnInit(): void {
    this.embeddedReportService.getReports();
    let sub = this.embeddedReportService.reports$.subscribe(() => {
      this.embeddedReportService.selectReport('KPI');
      sub?.unsubscribe();
    });
    const date = this.route.snapshot.paramMap.get('id');
    if (date) {
      this.isValidDate = moment(date).isValid();
      this.formattedDate = moment.utc(date).format('MM/DD/YYYY');
    }
    if (this.isValidDate) {
      this.waiting = true;
      this.querySubscription = this.apollo
        .watchQuery<any>({
          query: GET_COMMITTEE_DASHBOARD_BY_DATE,
          variables: {
            mixedDate: date,
            date: date,
            milestoneNames: [
              'Placement Committee Reviewed',
              'Capital Committee',
              'Screening Committee',
              'Closed'
            ],
          },
        })
        .valueChanges.subscribe(({ data }) => {
          if (data && data.hasOwnProperty('milestones')) {
            this.milestones = data.milestones.filter((milestone:Milestone) => milestone.name!=='Closed');
            this.closingMilestone = data.milestones.filter((milestone:Milestone) => milestone.name=='Closed')[0]
          }
          if (data && data.hasOwnProperty('committeeVoteTypes')) {
            this.voteTypes = data.committeeVoteTypes;
          }
          if (data && data.hasOwnProperty('committeeApprovalTypes')) {
            this.approvalTypes = data.committeeApprovalTypes;
            this.committeeItemsService.approvalTypes = this.approvalTypes
          }
          if(data){
            this.waiting = false;
            this.format(data);
            this.querySubscription.unsubscribe();
          }
        });
    }
  }
  state: string = 'default';
  rotate(i: number) {
    this.info[i].expand = !this.info[i].expand;
  }
  openReport(project_id: string) {
    this.embeddedReportService.setFilters({ project_id: project_id });
    this.embeddedReportService.setShow(true);
  }
  getDisclosureForMilestone(
    disclosures: ProjectDisclosure[],
    project: Project,
    milestone: Milestone
  ) {
    let dummyDisclosure = {
      id: 'new',
      project_id: project.id,
      milestone_id: milestone.id,
      disclosures: '',
      copy: '',
    };

    if (disclosures) {
      return (
        disclosures.find(
          (disclosure: ProjectDisclosure) =>
            disclosure.milestone_id === milestone.id
        ) || dummyDisclosure
      );
    }
    return dummyDisclosure;
  }
  format(data: any) {
    if (data && data.hasOwnProperty('pipelineMilestoneDates')) {
      let count = 0;
      for (let i = 0; i < data.pipelineMilestoneDates.length; i++) {
        for (
          let j = 0;
          j < data.pipelineMilestoneDates[i].pipelineProjectDates.length;
          j++
        ) {
          let project =
            data.pipelineMilestoneDates[i].pipelineProjectDates[j]
              .pipelineProject.project;
          let closingVote = project.committeeVotes.find(
            (vote:CommitteeVote) => vote.committeeVoteType?.name.toLowerCase() === 'legal'
                && vote.milestone?.name.toLowerCase() === 'closed')?.status
          let temp: any = {
            projectName: project.name,
            projectid: project.id,
            projecturl: this.dataFormat.formatLink(project),
            sharepointfolder: project.sharepoint_folder,
            islinked: project.is_synced_link,
            disclosures: [...project.projectDisclosures],
            projectStatus: '',
            expand: false,
            key: this.info.length,
            committeeDate: moment
              .utc(data.pipelineMilestoneDates[i].pipelineProjectDates[j].date)
              .format('MM/DD/YYYY'),
            committeeName:
              data.pipelineMilestoneDates[i].pipelineProjectDates[j]
                .pipelineMilestone.milestone.name,
            milestone:
              data.pipelineMilestoneDates[i].pipelineProjectDates[j]
                .pipelineMilestone.milestone,
            info: [],
            creditTypes: [],
            creditOutlets: [],
            project: project,
            committeeLinks: project.committeeLinks,
            votes: project.committeeVotes,
            readOnly:  closingVote !== undefined && closingVote !== null
          };
          this.committeeApprovalsService.receiveCommitteeApprovals(
            project.id,
            project.committeeApprovals
          );
          this.committeeItemsService.addItemsFromApi(
            project.id,
            project.committeeItems.filter((item:any)=>item.milestone_id!==this.closingMilestone.id) ?? []
          );
          if (project.projectStatuses !== null) {
            temp.projectStatus = project.projectStatuses[0].status.name;
          }

          //Closing Coordinator and Developer --- 2/5/25 -- changed developer to sponsor
          if (project.participants !== null) {
            if (
              this.dashboardService.getParticipant(
                project.participants,
                'Closing Coordinator'
              ) !== null
            ) {
              temp.info.push(
                this.dashboardService.getParticipant(
                  project.participants,
                  'Closing Coordinator'
                )
              );
            }

            let acquisitionsAnalyst;
            if (
              (acquisitionsAnalyst = this.dashboardService.getParticipant(
                project.participants,
                'Acquisition Analyst'
              ))
            ) {
              temp.info.push(acquisitionsAnalyst);
            } else {
              temp.info.push({ key: 'Acquisitions Analyst', value: 'N/A' });
            }

            if (
              this.dashboardService.getParticipant(
                project.participants,
                'Sponsor'
              ) !== null
            ) {
              temp.info.push(
                this.dashboardService.getParticipant(
                  project.participants,
                  'Sponsor'
                )
              );
            } else {
              temp.info.push({ key: 'Sponsor', value: 'N/A' });
            }
          }

          //   if (
          //     this.dashboardService.getParticipant(
          //       project.participants,
          //       'Developer'
          //     ) !== null
          //   ) {
          //     temp.info.push(
          //       this.dashboardService.getParticipant(
          //         project.participants,
          //         'Developer'
          //       )
          //     );
          //   } else {
          //     temp.info.push({ key: 'Developer/Sponsor', value: 'N/A' });
          //   }
          // }

          //Closing Date
          if (project.pipelineProjects !== null) {
            let closing = null;
            for (let k = 0; k < project.pipelineProjects.length; k++) {
              if (
                project.pipelineProjects[0].closingDate !== null &&
                project.pipelineProjects[0].closingDate.length > 0
              ) {
                closing = moment
                  .utc(project.pipelineProjects[0].closingDate[0].date)
                  .format('MM/DD/YYYY');
                temp.info.push({ key: 'Closing Date', value: closing });
              }
            }
            if (closing === null) {
              temp.info.push({ key: 'Closing Date', value: 'N/A' });
            }
          }



          // Account Manager
          if (
            this.dashboardService.getParticipant(
              project.participants,
              'Account Manager'
            ) !== null
          ) {
            temp.info.push(
              this.dashboardService.getParticipant(
                project.participants,
                'Account Manager'
              )
            );
          } else {
            temp.info.push({ key: 'Account Manager', value: 'N/A' });
          }

          //Credit Types and Credit Outlets
          let equity: number = 0;
          if (project.projectCredits !== null) {
            // temp.info.push({key: 'Credit Types', value: this.dataFormat.formatCreditTypes(project.projectCredits, false)});
            // temp.info.push({key: 'Credit Outlets', value: this.dataFormat.formatCreditOutlets(project.projectCredits)});
            temp.creditTypes = this.dataFormat.formatCreditTypes(
              project.projectCredits,
              false
            );
            temp.creditOutlets = this.dataFormat.formatCreditOutlets(
              project.projectCredits
            );
            equity = this.dashboardService.getLTEquity(project);
          }

          //Originator
          if (
            project.participants !== null &&
            this.dashboardService.getParticipant(
              project.participants,
              'Originator'
            ) !== null
          ) {
            temp.info.push(
              this.dashboardService.getParticipant(
                project.participants,
                'Originator'
              )
            );
          }
          temp.info.push({
            key: 'Repeat Deal',
            value: project.is_repeat ? 'Yes' : 'No',
          });
          //LTEquity
          temp.info.push({
            key: 'LT Equity',
            value: formatCurrency(equity, 'en-US', '$', undefined, '1.0-0'),
          });

          //Total Units
          if (!!project.properties && project.properties.length) {
            temp.info.push({
              key: 'Total Units',
              value: project.properties[0].total_units,
            });
          }

          //City/State
          if (
            !!project?.properties[0]?.entity?.entityAddresses[0]?.address
              ?.zipCode
          ) {
            temp.info.push({
              key: 'City/State',
              value:
                project.properties[0].entity.entityAddresses[0].address.zipCode
                  .city +
                ', ' +
                project.properties[0].entity.entityAddresses[0].address.zipCode
                  .state.abbreviation,
            });
          }
          let found = false;
          for (let n = 0; n < this.info.length; n += 1) {
            if (temp.projectid === this.info[n].projectid) {
              let key = this.info[n].key;
              temp.key = key;
              this.info[n] = temp;
              found = true;
              break;
            }
          }
          if (!found && temp.committeeName!=='Closed') {
            this.info.push(temp);
          }
        }
      }
    }
    this.info.sort((a: any, b: any) => {
      if (a.committeeName !== b.committeeName) {
        const committees: { [key: string]: number } = {
          'Placement Committee Reviewed': 1,
          'Screening Committee': 2,
          'Capital Committee': 3,
        };
        return committees[b.committeeName] - committees[a.committeeName];
      }
      if (a.projectName === b.projectName) {
        return 0;
      }
      return a.projectName > b.projectName ? 1 : -1;
    });
  }
  getLegalApprovalType(): CommitteeApprovalType | undefined {
    return this.approvalTypes.find(
      (approvalType: CommitteeApprovalType) => approvalType.name === 'Legal'
    );
  }

  receiveEditorHTML($event: ProjectDisclosure, i: number) {
    let index = this.info[i].disclosures.findIndex(
      (disclosure: ProjectDisclosure) =>
        disclosure.milestone_id === $event.milestone_id
    );
    if (index > -1) {
      this.info[i].disclosures[index] = $event;
    } else {
      this.info[i].disclosures.push($event);
    }
  }

  ngOnDestroy() {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
    this.embeddedReportService.setShow(false);
  }
}
